import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, Anchor, ArrowUpRight, Ship, GripVertical, Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const CARD_BG = ["bg-teal", "bg-sunny", "bg-orange text-white"];
const STATUS_LABEL = { new: "New", in_progress: "In progress", resolved: "Resolved" };

export default function ProjectsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status");
  const canEdit = ["owner", "admin", "member"].includes(user?.role);
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const load = () => apiClient.get("/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  const persistOrder = async (ordered) => {
    try { await apiClient.put("/projects/reorder", { order: ordered.map((p) => p.id) }); }
    catch { toast.error("Couldn't save the new order"); load(); }
  };
  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); setOverIndex(null); return; }
    const next = [...projects];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    setProjects(next);
    setDragIndex(null); setOverIndex(null);
    persistOrder(next);
  };
  const startRename = (p, e) => { e.stopPropagation(); setRenamingId(p.id); setRenameValue(p.name); };
  const saveRename = async (p) => {
    const nv = renameValue.trim();
    setRenamingId(null);
    if (!nv || nv === p.name) return;
    setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, name: nv } : x)));
    try { await apiClient.patch(`/projects/${p.id}/rename`, { name: nv }); toast.success("Project renamed"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); load(); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post("/projects", { name, client_name: clientName, description });
      toast.success("Project launched! 🚢");
      setOpen(false); setName(""); setClientName(""); setDescription("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="h-20 flex items-center justify-between px-6 sm:px-8">
        <div>
          <h1 className="font-display text-3xl font-700 tracking-tight">Projects</h1>
          <p className="text-sm text-ink/60">Each project is one client's beta deployment.{canEdit && projects && projects.length > 1 ? " Drag cards to reorder." : ""}</p>
          {statusFilter && STATUS_LABEL[statusFilter] && (
            <button
              data-testid="status-filter-pill"
              onClick={() => setSearchParams({})}
              className="mt-2 inline-flex items-center gap-2 text-xs font-800 px-3 py-1 rounded-full border-2 border-ink bg-sunny shadow-nb-sm hover:bg-sunny/80 transition-colors"
            >
              Filtering: {STATUS_LABEL[statusFilter]} <span className="text-ink/60">✕ clear</span>
            </button>
          )}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {canEdit && (
            <DialogTrigger asChild>
              <button data-testid="new-project-button" className="nb-btn bg-orange text-white px-5 py-2.5 text-sm">
                <Plus className="h-4 w-4" /> New project
              </button>
            </DialogTrigger>
          )}
          <DialogContent className="border-2 border-ink rounded-3xl shadow-nb-xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-700 tracking-tight">Launch a project</DialogTitle>
              <DialogDescription className="text-ink/60">Give your beta a name and tag which client it's for.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div>
                <label className="label-overline block mb-1.5">Project name</label>
                <input data-testid="project-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                  className="nb-input" placeholder="Mobile Banking Beta" />
              </div>
              <div>
                <label className="label-overline block mb-1.5">Client / deployment</label>
                <input data-testid="project-client-input" value={clientName} onChange={(e) => setClientName(e.target.value)}
                  className="nb-input" placeholder="Acme Bank" />
              </div>
              <div>
                <label className="label-overline block mb-1.5">Description</label>
                <textarea data-testid="project-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
                  className="nb-input" rows={2} placeholder="Optional" />
              </div>
              <DialogFooter>
                <button data-testid="create-project-submit" type="submit" disabled={saving}
                  className="nb-btn bg-orange text-white px-5 py-3 text-sm disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Launch project"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-6 lg:p-8 pt-2">
        {projects === null ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>
        ) : projects.length === 0 ? (
          <div className="nb-card p-16 text-center">
            <Anchor className="h-12 w-12 text-ink/20 mx-auto mb-4" />
            <h3 className="font-display font-700 text-2xl mb-1">No projects yet</h3>
            <p className="text-ink/60 mb-6 font-600">Launch your first project to grab an embeddable widget snippet.</p>
            {canEdit && <button onClick={() => setOpen(true)} data-testid="empty-new-project" className="nb-btn bg-orange text-white px-5 py-3 text-sm">Create a project</button>}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
            {projects.map((p, i) => (
              <div key={p.id} data-testid={`project-card-${p.id}`}
                draggable={canEdit && renamingId !== p.id}
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
                onDrop={() => handleDrop(i)}
                onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                onClick={() => { if (renamingId !== p.id) navigate(`/projects/${p.id}${statusFilter ? `?status=${statusFilter}` : ""}`); }}
                className={`nb-card nb-card-hover p-6 group cursor-pointer relative transition-all ${dragIndex === i ? "opacity-40" : ""} ${overIndex === i && dragIndex !== null && dragIndex !== i ? "ring-4 ring-orange/40" : ""}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className={`h-12 w-12 ${CARD_BG[i % CARD_BG.length]} border-2 border-ink rounded-2xl flex items-center justify-center shadow-nb-sm`}>
                    <Ship className="h-6 w-6" />
                  </div>
                  <div className="flex items-center gap-0.5">
                    {canEdit && (
                      <button data-testid={`rename-project-${p.id}`} onClick={(e) => startRename(p, e)} title="Rename project"
                        className="p-1.5 rounded-lg text-ink/40 hover:text-orange hover:bg-sand transition-colors"><Pencil className="h-4 w-4" /></button>
                    )}
                    {canEdit && (
                      <span data-testid={`drag-handle-${p.id}`} title="Drag to reorder" onClick={(e) => e.stopPropagation()}
                        className="p-1.5 text-ink/30 cursor-grab active:cursor-grabbing"><GripVertical className="h-4 w-4" /></span>
                    )}
                    <ArrowUpRight className="h-5 w-5 text-ink/40 group-hover:text-orange transition-colors" />
                  </div>
                </div>
                {renamingId === p.id ? (
                  <input autoFocus data-testid={`rename-input-${p.id}`} value={renameValue}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRename(p); if (e.key === "Escape") setRenamingId(null); }}
                    onBlur={() => saveRename(p)}
                    className="nb-input text-lg font-700 w-full mb-1" maxLength={80} />
                ) : (
                  <h3 className="font-display font-700 text-xl tracking-tight">{p.name}</h3>
                )}
                {p.client_name && <p className="label-overline mt-1">{p.client_name}</p>}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t-2 border-ink/10 text-sm font-700">
                  <span><span className="font-mono">{p.feedback_count}</span> <span className="text-ink/50">total</span></span>
                  {p.new_count > 0 && <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border-2 border-ink bg-danger" /><span className="font-mono">{p.new_count}</span> <span className="text-ink/50">new</span></span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
